import React, { useMemo } from 'react';
import useSWR from 'swr';
import { format, subDays } from 'date-fns';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

import { useShop } from '@services/ShopProvider';
import { getMultipleInsights } from '@services/insights';
import { useInsightsDate } from '@services/InsightsDateProvider';
import { usePlansForShopIds } from '@services/localAds';
import { getTotalAdsSpend } from '@utils/localAdsPlans';
import calculateCpc from '@utils/calcCpc';

import { JourneyCard } from '../../components/JourneyCard';
import { EmptyState } from '../../components/EmptyState';
import { StatBadge, comparisonLabel } from '../../components/StatBadge';
import { CARD_STATES, useCardEmptyState } from '../../hooks/useCardEmptyState';

import styles from './LocalAdsCard.module.scss';

const formatCompact = (n) => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
	if (n >= 10_000) return `${Math.round(n / 1_000)}k`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
	return n.toLocaleString();
};

const formatPercent = (n) => `${n.toFixed(2)}%`;

const formatCurrency = (n, currency) =>
	n.toLocaleString('en', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCurrencyCompact = (n, currency) => {
	const symbol = (0).toLocaleString('en', { style: 'currency', currency }).replace(/[\d.,\s]/g, '');
	if (n >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(1)}m`;
	if (n >= 10_000) return `${symbol}${Math.round(n / 1_000)}k`;
	if (n >= 1_000) return `${symbol}${(n / 1_000).toFixed(1)}k`;
	return n.toLocaleString('en', { style: 'currency', currency, maximumFractionDigits: 0 });
};

const formatCurrencyWhole = (n, currency) => n.toLocaleString('en', { style: 'currency', currency, maximumFractionDigits: 0 });

const sumRow = (rows, key) => (rows ?? []).reduce((acc, r) => acc + (r[key] ?? 0), 0);

const pctDelta = (current, previous) => {
	if (!previous) return null;
	const v = ((current - previous) / previous) * 100;
	return Math.round(v * 10) / 10;
};

/**
 * Local Ads card — Clicks / CTR / CPC over the selected period, each with a
 * delta vs the same-length comparison window (CPC inverts the badge colour —
 * a lower cost-per-click is an improvement). Impressions area sparkline below.
 * Bottom block carries the budget context: total spend for the period and
 * today's daily budget rate.
 */
export const LocalAdsCard = () => {
	const { data: shops, shop } = useShop();
	const scopeShops = shop?.id ? [shop] : (shops ?? []);
	const shopIds = scopeShops.map((s) => s.id);
	const { startDate, endDate, comparison, interval, selectedRange } = useInsightsDate();

	const localAdsBase = {
		table: 'local_ads',
		interval,
		id: shopIds,
		columns: ['clicks', 'impressions', 'cpc', 'date'],
		sum: ['clicks', 'impressions'],
		avg: ['cpc'],
		sort: [{ column: 'date', order: 'asc' }]
	};

	const queries = shopIds.length
		? [
				{ ...localAdsBase, from: format(startDate, 'yyyy-MM-dd'), to: format(endDate, 'yyyy-MM-dd') },
				{ ...localAdsBase, from: format(comparison.startDate, 'yyyy-MM-dd'), to: format(comparison.endDate, 'yyyy-MM-dd') }
			]
		: null;

	const swrKey = queries ? ['local-ads-multi', JSON.stringify(queries)] : null;
	const { data: resultSet, isLoading } = useSWR(swrKey, () => getMultipleInsights(queries));
	const [currentRows, prevRows] = resultSet ?? [[], []];

	const plans = usePlansForShopIds(shopIds);
	const currentSpend = useMemo(() => getTotalAdsSpend(plans, startDate, endDate, scopeShops), [plans, startDate, endDate, scopeShops]);
	const currency = scopeShops.find((s) => s.billingCurrency)?.billingCurrency ?? shops?.[0]?.billingCurrency ?? 'GBP';

	// Today's daily budget — sum of `budget` on plans currently live across the
	// org's shops. Tells the retailer exactly what they're spending each day.
	const livePlans = (plans ?? []).filter((p) => p.status === 'live');
	const todayBudget = livePlans.reduce((acc, p) => acc + (p.budget ?? 0), 0);
	// Chain context: how many shops currently have ads running, vs total in
	// the scope. A retailer running 12/36 wants to know without leaving the
	// dashboard.
	const liveShopIds = new Set(livePlans.map((p) => p.shop));
	const liveLocationCount = liveShopIds.size;
	const isChainView = !shop?.id;

	// Walk backwards day-by-day from today; stop at the first gap (no plan, or
	// budget 0). The earliest budgeted day in the current consecutive run is
	// when this campaign started — gives a "Running since 28 Apr" anchor.
	const runStartDate = (() => {
		if (!plans?.length) return null;
		const budgetedDates = new Set(plans.filter((p) => (p.budget ?? 0) > 0).map((p) => p.date));
		let day = new Date();
		let earliest = null;
		for (let i = 0; i < 365; i++) {
			const key = format(day, 'yyyy-MM-dd');
			if (budgetedDates.has(key)) earliest = key;
			else if (earliest) break; // hit a gap after at least one budgeted day
			day = subDays(day, 1);
		}
		return earliest ? new Date(earliest) : null;
	})();

	const totals = {
		impressions: sumRow(currentRows, 'impressions'),
		clicks: sumRow(currentRows, 'clicks'),
		spend: currentSpend?.spend ?? 0
	};
	const prev = {
		impressions: sumRow(prevRows, 'impressions'),
		clicks: sumRow(prevRows, 'clicks')
	};

	// Match the standalone Local Ads insights page (calcCpc): mean of daily cpc
	// values where cpc > 0. Returns a fixed-2 string; parseFloat for numerics.
	const cpc = parseFloat(calculateCpc(currentRows));
	const prevCpc = parseFloat(calculateCpc(prevRows));
	const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
	const prevCtr = prev.impressions > 0 ? (prev.clicks / prev.impressions) * 100 : 0;

	const deltas = {
		clicks: pctDelta(totals.clicks, prev.clicks),
		ctr: pctDelta(ctr, prevCtr),
		cpc: pctDelta(cpc, prevCpc)
	};

	const dataIsEmpty = !isLoading && totals.impressions === 0 && totals.clicks === 0;
	const emptyState = useCardEmptyState({
		cardId: 'localAds',
		requiresAds: true,
		dataIsEmpty
	});

	// Skeleton while the SWR call hasn't returned yet — prevents the zeros from
	// flashing as the empty state during initial load.
	const showLoading = isLoading && !resultSet;

	return (
		<JourneyCard
			title="Local Ads"
			subtitle="Paid promotion of your in-store inventory"
			ctaLabel="Manage Local Inventory Ads"
			ctaHref={shop?.id ? `/insights/${shop.id}/local-ads` : '/insights/local-ads'}
		>
			{showLoading ? (
				<LocalAdsCardSkeleton />
			) : emptyState.state !== CARD_STATES.DATA ? (
				<EmptyState state={emptyState.state} copy={emptyState.copy} ctaLabel={emptyState.ctaLabel} ctaHref={emptyState.ctaHref} />
			) : (
				<>
					<div className={styles.periodSection}>
						<h3 className={styles.periodHeading}>{selectedRange}</h3>
						<div className={styles.kpiRow}>
							<div className={styles.kpi}>
								<div className={styles.figure}>{formatCompact(totals.clicks)}</div>
								<div className={styles.kpiLabel}>Clicks</div>
								{deltas.clicks !== null && (
									<div className={styles.deltaWrap}>
										<StatBadge value={deltas.clicks} positive tooltip={comparisonLabel(comparison)} />
									</div>
								)}
							</div>
							<div className={styles.kpi}>
								<div className={styles.figure}>{formatPercent(ctr)}</div>
								<div className={styles.kpiLabel}>CTR</div>
								{deltas.ctr !== null && (
									<div className={styles.deltaWrap}>
										<StatBadge value={deltas.ctr} positive tooltip={comparisonLabel(comparison)} />
									</div>
								)}
							</div>
							<div className={styles.kpi}>
								<div className={styles.figure}>{formatCurrency(cpc, currency)}</div>
								<div className={styles.kpiLabel}>CPC</div>
								{deltas.cpc !== null && (
									<div className={styles.deltaWrap}>
										<StatBadge value={deltas.cpc} positive={false} tooltip={comparisonLabel(comparison)} />
									</div>
								)}
							</div>
						</div>

						{currentRows.length > 1 && (
							<div className={styles.chart}>
								<ResponsiveContainer width="100%" height="100%">
									<AreaChart data={currentRows} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
										<defs>
											<linearGradient id="local-ads-grad" x1="0" y1="0" x2="0" y2="1">
												<stop offset="0%" stopColor="var(--blue)" stopOpacity={0.3} />
												<stop offset="100%" stopColor="var(--blue)" stopOpacity={0} />
											</linearGradient>
										</defs>
										<Area
											type="monotone"
											dataKey="impressions"
											stroke="var(--blue)"
											strokeWidth={2.5}
											fill="url(#local-ads-grad)"
											isAnimationActive={false}
										/>
									</AreaChart>
								</ResponsiveContainer>
							</div>
						)}

						<p className={styles.contextLine}>{formatCurrencyCompact(totals.spend, currency)} spent</p>
					</div>

					<div className={styles.budgetSection}>
						<div className={styles.budgetHeader}>
							<h3 className={styles.subheading}>Budget</h3>
							{todayBudget > 0 ? (
								<span className={`${styles.statusPill} ${styles.statusPillActive}`}>
									<span className={styles.statusDot} aria-hidden="true" />
									Active
								</span>
							) : (
								<span className={`${styles.statusPill} ${styles.statusPillPaused}`}>Paused</span>
							)}
						</div>
						<div className={styles.budgetValue}>
							{todayBudget > 0 ? `${formatCurrencyWhole(todayBudget, currency)} per day` : '—'}
						</div>
						{todayBudget > 0 && (
							<p className={styles.contextLine}>
								{isChainView && liveLocationCount > 0 && (
									<>
										Running in {liveLocationCount} of {scopeShops.length}{' '}
										{scopeShops.length === 1 ? 'location' : 'locations'}
									</>
								)}
								{isChainView && liveLocationCount > 0 && runStartDate && ' · '}
								{runStartDate && <>Since {format(runStartDate, 'd MMM')}</>}
							</p>
						)}
					</div>
				</>
			)}
		</JourneyCard>
	);
};

const LocalAdsCardSkeleton = () => (
	<>
		<div className={styles.periodSection}>
			<div className={`${styles.skeletonBlock} ${styles.skeletonPeriodHeading}`} />
			<div className={styles.kpiRow}>
				{[0, 1, 2].map((i) => (
					<div key={i} className={styles.kpi}>
						<div className={`${styles.skeletonBlock} ${styles.skeletonFigure}`} />
						<div className={`${styles.skeletonBlock} ${styles.skeletonKpiLabel}`} />
						<div className={`${styles.skeletonBlock} ${styles.skeletonDelta}`} />
					</div>
				))}
			</div>
			<div className={`${styles.chart} ${styles.skeletonChart}`} />
			<div className={`${styles.skeletonBlock} ${styles.skeletonContextLine}`} />
		</div>
		<div className={styles.budgetSection}>
			<div className={styles.budgetHeader}>
				<div className={`${styles.skeletonBlock} ${styles.skeletonSubheading}`} />
				<div className={`${styles.skeletonBlock} ${styles.skeletonStatusPill}`} />
			</div>
			<div className={`${styles.skeletonBlock} ${styles.skeletonBudgetValue}`} />
			<div className={`${styles.skeletonBlock} ${styles.skeletonContextLine}`} />
		</div>
	</>
);
